import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { FaCheckCircle } from 'react-icons/fa';
import { firmatContratoThunk } from '../../store/slices/public/procesoMatriculacion.slice';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';

const ContratoViewer = ({ isOpen, onClose, contrato, datosUsuario, contratoId }) => {
    const dispatch = useDispatch();
    const [autorizacion, setAutorizacion] = useState('');
    const [respuestaDos, setRespuestaDos] = useState('');
    const [aceptacion, setAceptacion] = useState('');
    const [desistimiento, seDesistimiento] = useState('');
    const [nombreAutorizador, setNombreAutorizador] = useState('');
    const [cedulaAutorizador, setCedulaAutorizador] = useState('');
    const [file, setFile] = useState();
    const [cardFile, setCardFile] = useState();
    const [errors, setErrors] = useState({});

    useEffect(() => {
        setAutorizacion('Sí autorizo');
        setRespuestaDos('Sí');
        setAceptacion('Sí acepto');
        seDesistimiento('No desisto');
        setNombreAutorizador('');
        setCedulaAutorizador('');
    }, [contrato]);

    // Función que agrega target="_blank" y rel="noopener noreferrer" a todos los links
    const addTargetToLinks = (html) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        const links = tempDiv.querySelectorAll('a');
        links.forEach(link => {
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
        });

        return tempDiv.innerHTML;
    };

    const renderContratoConDatos = (template, data) => {
        return template.replace(/\{(.*?)\}/g, (_, key) => {
            const trimmedKey = key.trim();
            if (['autorizacion', 'autorizacion_dos', 'acepta', 'desisto', 'datos_persona', 'datos_aseguradora'].includes(trimmedKey)) {
                return `{${trimmedKey}}`;
            }
            return data[trimmedKey] || '';
        });
    };

    const contieneDesistimiento = contrato?.content.includes('{desisto}') || contrato?.clauses?.some(c => c.text.includes('{desisto}'));
    const contieneAutorizacion = contrato?.content.includes('{autorizacion}') || contrato?.clauses?.some(c => c.text.includes('{autorizacion}'));
    const contieneAutorizacionDos = contrato?.content.includes('{autorizacion_dos}') || contrato?.clauses?.some(c => c.text.includes('{autorizacion_dos}'));
    const contieneDatosPersona = contrato?.content.includes('{datos_persona}') || contrato?.clauses?.some(c => c.text.includes('{datos_persona}'));
    const contieneDatosAseguradora = contrato?.content.includes('{datos_aseguradora}') || contrato?.clauses?.some(c => c.text.includes('{datos_aseguradora}'));

    const validateFields = () => {
        const newErrors = {};

        if (contieneDesistimiento && desistimiento === 'Sí desisto') {
            if (!file || file.length === 0) {
                newErrors.file = 'Debe subir un archivo de respaldo.';
            }
            if (contieneDatosAseguradora && !cardFile) {
                newErrors.aseguradora = 'Debe subir carta desistimiento';
            }
        }

        if (contieneAutorizacion && autorizacion === 'Sí autorizo') {
            if (contieneDatosPersona && !nombreAutorizador.trim()) {
                newErrors.nombreAutorizador = 'Ingrese nombre de quien autoriza';
            }
            if (contieneDatosPersona && !cedulaAutorizador.trim()) {
                newErrors.cedulaAutorizador = 'Ingrese cédula de quien autoriza';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleSign = () => {
        if (!validateFields()) {
            const errorHtml = Object.values(errors)
                .map(err => `<p style="color:red; font-size:14px; margin:4px 0;">• ${err}</p>`)
                .join('');

            Swal.fire({
                icon: 'error',
                title: 'Faltan datos obligatorios',
                html: ` <div style="text-align:left;"> ${errorHtml}</div>
    `
            });
            return;
        }

        Swal.fire({
            title: '¿Está seguro de que desea firmar el contrato?',
            text: 'Certifica que ha leído y acepta el contrato.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#9DC41A',
            cancelButtonColor: '#9ca3af',
            confirmButtonText: 'Firmar y enviar',
            cancelButtonText: 'Cancelar'
        }).then(result => {
            if (result.isConfirmed) {
                let contenidoCompleto = contratoRenderizado;
                contrato.clauses?.forEach(clause => {
                    const texto = renderContratoConDatos(clause.text, datosUsuario);
                    contenidoCompleto += `<div>${texto}</div>`;
                });

                let respuesta = '';

                if (contrato.content.includes('{acepta}') || contrato.clauses?.some(c => c.text.includes('{acepta}'))) {
                    respuesta = aceptacion;
                } else if (contrato.content.includes('{autorizacion}') || contrato.clauses?.some(c => c.text.includes('{autorizacion}'))) {
                    respuesta = autorizacion;
                } else if (contrato.content.includes('{desisto}') || contrato.clauses?.some(c => c.text.includes('{desisto}'))) {
                    respuesta = desistimiento;
                }

                const formData = new FormData();
                formData.append('personaAutorizada', nombreAutorizador);
                formData.append('ciPersonaAutorizada', cedulaAutorizador);
                if (file) formData.append('attachedFile', file[0]);
                if (cardFile) formData.append('cardFile', cardFile[0]);
                formData.append('respuesta', respuesta);
                formData.append('respuesta_dos', respuestaDos);
                formData.append('siggned', true);
                formData.append('contenidoContrato', contenidoCompleto);

                dispatch(firmatContratoThunk(contratoId, formData));
            }
        });
    };

    const renderPlaceholdersInputs = (texto) => {
        const contieneAutorizacion = texto.includes('{autorizacion}');
        const contieneAutorizacionDos = texto.includes('{autorizacion_dos}');
        const contieneAceptacion = texto.includes('{acepta}');
        const contieneDesistimiento = texto.includes('{desisto}');
        const contieneDatosPersona = texto.includes('{datos_persona}');
        const contieneDatosAseguradora = texto.includes('{datos_aseguradora}');

        const contenidoHTML = texto
            .replace('{autorizacion}', '<div id="autorizacion-placeholder"></div>')
            .replace('{autorizacion_dos}', '<div id="autorizacion-dos-placeholder"></div>')
            .replace('{acepta}', '<div id="aceptacion-placeholder"></div>')
            .replace('{desisto}', '<div id="desistimineto-placeholder"></div>')
            .replace('{datos_persona}', '<div id="datos-persona-placeholder"></div>')
            .replace('{datos_aseguradora}', '<div id="datos-persona-placeholder"></div>');

        return (
            <>
                <div
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(contenidoHTML)
                    }}
                />

                {contieneAceptacion && (
                    <div className="space-y-2 mt-2">
                        <label className="block text-sm font-medium text-red-500">¿Acepta?</label>
                        <select
                            value={aceptacion}
                            onChange={(e) => setAceptacion(e.target.value)}
                            className="w-full border border-sky-500 rounded-md px-3 py-2"
                        >
                            <option value="Sí acepto">Sí acepto</option>
                            <option value="No acepto">No acepto</option>
                        </select>
                    </div>
                )}

                {contieneDesistimiento && (
                    <div className="space-y-2 mt-2">
                        <label className="block text-sm font-medium text-red-500">¿Desiste?</label>
                        <select
                            value={desistimiento}
                            onChange={(e) => seDesistimiento(e.target.value)}
                            className="w-full border border-sky-500 rounded-md px-3 py-2"
                        >
                            <option value="No desisto">No desisto</option>
                            <option value="Sí desisto">Sí desisto</option>
                        </select>
                        {contieneDesistimiento && desistimiento === "Sí desisto" && (
                            <div className="space-y-2 mt-2">
                                <input
                                    required
                                    type="file"
                                    name='file'
                                    accept=".pdf,.jpg,.png"
                                    onChange={(e) => setFile(e.target.files)}
                                    className="cursor-pointer w-full focus:outline-none focus:ring-2 focus:ring-blue-400 file:bg-blue-50 file:border file:mr-3 file:py-1 file:px-3 file:rounded-md file:text-sm file:font-medium file:text-blue-700"
                                />
                                <span className='text-[11px] text-red-500'>Por favor, cargue el respaldo o certificado vigente</span>
                            </div>
                        )}
                        {contieneDatosAseguradora && desistimiento === "Sí desisto" && (
                            <div className="space-y-2 mt-2">
                                <input
                                    required
                                    type="file"
                                    name='cardFile'
                                    accept=".pdf,.jpg,.png"
                                    onChange={(e) => setCardFile(e.target.files)}
                                    className="cursor-pointer w-full focus:outline-none focus:ring-2 focus:ring-blue-400 file:bg-blue-50 file:border file:mr-3 file:py-1 file:px-3 file:rounded-md file:text-sm file:font-medium file:text-blue-700"
                                />
                                <span className='text-[11px] text-red-500'>Por favor, carta de desistimiento firmada</span>
                            </div>
                        )}
                    </div>
                )}

                {contieneAutorizacion && (
                    <div className="space-y-2 mt-2">
                        <label className="block text-sm font-medium text-red-500">¿Autoriza?</label>
                        <select
                            value={autorizacion}
                            onChange={(e) => setAutorizacion(e.target.value)}
                            className="w-full border border-sky-500 rounded-md px-3 py-2"
                        >
                            <option value="Sí autorizo">Sí autorizo</option>
                            <option value="No autorizo">No autorizo</option>
                        </select>

                        {contieneDatosPersona && autorizacion === "Sí autorizo" && (
                            <div className="space-y-2 mt-2">
                                <input
                                    required
                                    type="text"
                                    placeholder="Nombre de quien autoriza"
                                    value={nombreAutorizador}
                                    onChange={(e) => setNombreAutorizador(e.target.value)}
                                    className="w-full border border-sky-300 rounded-md px-3 py-2"
                                />
                                <input
                                    required
                                    type="text"
                                    placeholder="Cédula de quien autoriza"
                                    value={cedulaAutorizador}
                                    onChange={(e) => setCedulaAutorizador(e.target.value)}
                                    className="w-full border border-sky-300 rounded-md px-3 py-2"
                                />
                            </div>
                        )}
                    </div>
                )}

                {contieneAutorizacionDos && (
                    <div className="space-y-2 mt-2">
                        <label className="block text-sm font-medium text-red-500">¿Autoriza?</label>
                        <select
                            value={respuestaDos}
                            onChange={(e) => setRespuestaDos(e.target.value)}
                            className="w-full border border-sky-500 rounded-md px-3 py-2"
                        >
                            <option value="Sí">Sí</option>
                            <option value="No">No</option>
                        </select>
                    </div>
                )}
            </>
        );
    };


    if (!isOpen || !contrato) return null;

    const contratoRenderizado = renderContratoConDatos(contrato.content, datosUsuario);
    const contratoConLinks = addTargetToLinks(contratoRenderizado);
    const contenidoSeguro = DOMPurify.sanitize(contratoConLinks, {
        ADD_ATTR: ['target', 'rel']
    });
    return (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center">
            <div className="bg-white h-full mx-auto border border-gray-200 w-full max-w-4xl max-h-[90vh] rounded-lg shadow-xl p-6 relative">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">{contrato?.name}</h2>
                <div className="text-start h-[68%] overflow-y-auto border border-gray-100 rounded-lg p-4 text-sm text-gray-700 bg-gray-50 space-y-3 mb-6">
                    <div dangerouslySetInnerHTML={{ __html: contenidoSeguro }} />
                    {
                        contrato.clauses?.map((clause, index) => {
                            const texto = renderContratoConDatos(clause.text, datosUsuario);
                            return (
                                <div key={index} className="text-start prose max-w-none mb-6 text-sm text-gray-700">
                                    {renderPlaceholdersInputs(texto)}
                                </div>
                            );
                        })
                    }
                </div>
                {!contrato.firmado ? (
                    <div className='space-y-2'>
                        <button
                            onClick={handleSign}
                            className="w-full py-2 px-4 rounded-md text-white font-semibold transition bg-p-sky/70 hover:bg-p-sky"
                        >
                            Firmar y enviar
                        </button>
                        <button
                            className="w-full py-2 px-4 rounded-md text-white font-semibold transition bg-gray-400 hover:bg-gray-500"
                            onClick={onClose}
                        >
                            Atrás
                        </button>
                    </div>
                ) : (
                    <div className='flex items-center justify-center gap-5 text-p-green'>
                        <FaCheckCircle size={50} />
                        <div className='flex flex-col text-start'>
                            <h2 className='text-xl font-semibold'>Contrato firmado</h2>
                            <p className='text-p-blue'>Muchas gracias por su tiempo.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContratoViewer;
